import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getStageUxConfig } from "./funnelStages";

function stageLabel(stageId) {
  const map = {
    acquisition: "Aquisição",
    capture: "Captura",
    entry: "Entrada",
    activation: "Ativação",
    conversion: "Conversão",
    expansion: "Expansão",
    retention: "Retenção",
    recovery: "Recuperação",
  };
  return map[stageId] || stageId || "Etapa";
}

function stageHints(stageId) {
  const map = {
    acquisition: ["tráfego pago", "cold email", "integrações", "campanhas externas"],
    capture: ["landing pages", "formulários", "tracking"],
    entry: ["signup automático", "criação de lead"],
    activation: ["onboarding", "chatbot", "diagnóstico", "agentes de IA"],
    conversion: ["plano sugerido", "oferta", "pagamento"],
    expansion: ["upsell", "cross-sell"],
    retention: ["alertas", "relatórios", "acompanhamento"],
    recovery: ["email", "remarketing", "reengajamento"],
  };
  return map[stageId] || [];
}

export default function StageConfigPanel({ open, stageId, flowConfig, onClose, onChange, onSave }) {
  const cfg = useMemo(() => getStageUxConfig(flowConfig, stageId), [flowConfig, stageId]);

  const [localError, setLocalError] = useState("");

  const enabled = open && !!stageId;
  const title = stageLabel(stageId);
  const hints = stageHints(stageId);

  const channel = cfg.channel || "";
  const integration = cfg.integration || "";
  const offer = cfg.offer || "";
  const notes = cfg.notes || "";
  const mode = cfg.mode || "simple";

  return (
    <Drawer anchor="right" open={!!enabled} onClose={onClose}>
      <Box sx={{ width: { xs: 340, sm: 420 }, p: 3 }}>
        <Stack spacing={1}>
          <Typography variant="h6" fontWeight={900}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configuração de UX por etapa (salva em <code>config.uxFunnel</code>).
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {localError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        ) : null}

        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Modo</InputLabel>
            <Select
              label="Modo"
              value={mode}
              onChange={(e) => {
                setLocalError("");
                onChange?.({ mode: String(e.target.value) });
              }}
            >
              <MenuItem value="simple">Simples</MenuItem>
              <MenuItem value="advanced">Avançado</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Canal / origem (opcional)"
            value={channel}
            onChange={(e) => onChange?.({ channel: e.target.value })}
            placeholder="ex.: instagram"
            fullWidth
          />

          <TextField
            label="Integração (opcional)"
            value={integration}
            onChange={(e) => onChange?.({ integration: e.target.value })}
            placeholder="ex.: meta, rdstation, stripe"
            fullWidth
          />

          {stageId === "conversion" ? (
            <TextField
              label="Oferta / plano sugerido (opcional)"
              value={offer}
              onChange={(e) => onChange?.({ offer: e.target.value })}
              placeholder="ex.: Plano Pro + 7 dias grátis"
              fullWidth
            />
          ) : null}

          <TextField
            label="Notas"
            value={notes}
            onChange={(e) => onChange?.({ notes: e.target.value })}
            fullWidth
            multiline
            minRows={4}
          />

          {hints.length ? (
            <Alert severity="info">
              <strong>Checklist sugerido</strong>
              <div>{hints.map((h) => `• ${h}`).join("\n")}</div>
            </Alert>
          ) : null}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
          <Button variant="outlined" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                setLocalError("");
                await onSave?.();
                onClose?.();
              } catch (e) {
                setLocalError("Não foi possível salvar agora. Tente novamente.");
              }
            }}
          >
            Salvar
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}

